import { supabase } from './supabaseClient';

const DEFAULT_FREE_SHARED_SLOT_LIMIT = 25;

function normalizePositiveInteger(raw: unknown, fallback: number): number {
  const value = Math.trunc(Number(raw));
  if (!Number.isFinite(value) || value <= 0) return fallback;
  return value;
}

export const FREE_SHARED_SLOT_LIMIT = normalizePositiveInteger(
  import.meta.env.VITE_FREE_INVENTORY_LIMIT,
  DEFAULT_FREE_SHARED_SLOT_LIMIT
);

type PlanLimitRow = {
  tier?: string | null;
  inventory_slots?: number | null;
} | null;

export type SharedSlotStatus = {
  usedSlots: number;
  limit: number | null;
  isPro: boolean;
  isOverLimit: boolean;
};

function resolveLimitFromPlanRow(row: PlanLimitRow): number | null {
  const tier = String(row?.tier ?? 'free').toLowerCase();
  if (tier === 'pro') return null;

  const planLimit = row?.inventory_slots;
  if (planLimit == null) return FREE_SHARED_SLOT_LIMIT;
  return Math.min(
    normalizePositiveInteger(planLimit, FREE_SHARED_SLOT_LIMIT),
    FREE_SHARED_SLOT_LIMIT
  );
}

/**
 * Returns the effective shared slot limit (inventory + wishlist) for the user.
 * null means unlimited (Pro).
 */
export async function getSharedSlotLimitForUser(userId: string): Promise<number | null> {
  const { data, error } = await supabase
    .from('v_user_plan')
    .select('tier, inventory_slots')
    .eq('user_id', userId)
    .maybeSingle();
  if (error) throw error;

  const row = data as PlanLimitRow;
  return resolveLimitFromPlanRow(row);
}

async function countRowsForUser(table: 'cards_inventory' | 'wishlist', userId: string): Promise<number> {
  const { count, error } = await supabase
    .from(table)
    .select('*', { count: 'exact', head: true })
    .eq('user_id', userId);
  if (error) throw error;
  return count ?? 0;
}

export async function getUsedSharedSlotsForUser(userId: string): Promise<number> {
  const [inventoryCount, wishlistCount] = await Promise.all([
    countRowsForUser('cards_inventory', userId),
    countRowsForUser('wishlist', userId),
  ]);
  return inventoryCount + wishlistCount;
}

export async function getSharedSlotStatusForUser(userId: string): Promise<SharedSlotStatus> {
  const { data, error } = await supabase
    .from('v_user_plan')
    .select('tier, inventory_slots')
    .eq('user_id', userId)
    .maybeSingle();
  if (error) throw error;

  const row = data as PlanLimitRow;
  const tier = String(row?.tier ?? 'free').toLowerCase();
  const isPro = tier === 'pro';
  const limit = resolveLimitFromPlanRow(row);
  const usedSlots = await getUsedSharedSlotsForUser(userId);
  const isOverLimit = !isPro && limit != null && usedSlots > limit;

  return {
    usedSlots,
    limit,
    isPro,
    isOverLimit,
  };
}

export async function assertCanCreateSharedSlot(userId: string): Promise<void> {
  const status = await getSharedSlotStatusForUser(userId);
  const { limit, usedSlots } = status;
  if (limit == null) return;

  if (usedSlots >= limit) {
    throw new Error(
      `Limite slot raggiunto (${limit} totali tra Inventario e Wishlist). Passa a Pro per slot illimitati.`
    );
  }
}
